import React, { useEffect, useRef, useState } from 'react';
import api from '../utils/api';
import { MapPin, Signal, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

/**
 * GPSActiveBanner — Iter-263
 *
 * Drop-in banner + auto-tracker that the talent sees on any "active shift"
 * surface while they're clocked in. It:
 *
 *  1. Calls POST /api/gps/start-tracking when mounted for an active booking.
 *  2. Records a breadcrumb every 90 seconds (config: GPSActiveBanner.interval).
 *  3. On geolocation errors (permission revoked, timeout, etc.), calls
 *     POST /api/gps/report-disabled so the shift is flagged for admin review
 *     and Instant Pay is paused until the admin clears it.
 *  4. Shows a persistent visible banner reinforcing consent and transparency.
 *  5. Stops automatically when the parent unmounts (clock-out flow handles
 *     POST /api/gps/stop-tracking via the existing checkout endpoint).
 *
 * Props:
 *   bookingId  string — the active booking id
 *   active     boolean — true while the talent is clocked in
 *   onError    optional callback(err) when GPS is denied
 *   interval   optional override (ms). Defaults to 90s.
 */
const RECORD_INTERVAL_MS = 90_000; // 90 seconds — feasibility doc recommendation

const GPSActiveBanner = ({ bookingId, active, onError, interval = RECORD_INTERVAL_MS }) => {
  const [lastFix, setLastFix] = useState(null);
  const [error, setError] = useState(null);
  const [pointCount, setPointCount] = useState(0);
  const [reported, setReported] = useState(false);
  const timerRef = useRef(null);
  const startedRef = useRef(false);

  const reportDisabled = async (reason, detail) => {
    if (reported || !bookingId) return;
    setReported(true);
    try {
      await api.post('/gps/report-disabled', {
        booking_id: bookingId,
        reason,
        detail,
      });
      toast.warning(
        'GPS issue logged — your shift was flagged for admin review. Instant Pay will be paused until the review is complete.',
        { duration: 8000 }
      );
    } catch (e) {
      // best-effort
    }
    if (onError) onError({ reason, detail });
  };

  const recordOne = () => {
    if (!('geolocation' in navigator)) {
      setError('Geolocation not supported by this browser');
      reportDisabled('not_supported', 'navigator.geolocation undefined');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await api.post(`/gps/record?booking_id=${bookingId}`, {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy_meters: pos.coords.accuracy,
            altitude_meters: pos.coords.altitude,
          });
          setLastFix({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
            at: new Date(),
          });
          setPointCount((n) => n + 1);
          setError(null);
        } catch (e) {
          // backend error - don't penalise talent for transient API errors
        }
      },
      (err) => {
        const reason =
          err.code === 1
            ? 'permission_denied'
            : err.code === 2
              ? 'position_unavailable'
              : err.code === 3
                ? 'timeout'
                : 'other';
        setError(
          err.code === 1
            ? 'Location permission was denied. Re-enable Location Services in your device settings.'
            : 'Unable to determine your location.'
        );
        reportDisabled(reason, err.message);
      },
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 30_000 }
    );
  };

  // Start the tracking session once when the banner becomes active
  useEffect(() => {
    if (!active || !bookingId || startedRef.current) return;
    startedRef.current = true;
    (async () => {
      try {
        await api.post('/gps/start-tracking', { booking_id: bookingId });
      } catch (e) {
        // 409/active already — ignore
      }
      recordOne(); // first fix immediately
      timerRef.current = setInterval(recordOne, interval);
    })();

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, bookingId, interval]);

  if (!active) return null;

  return (
    <div
      className="rounded-lg border-2 border-cyan-500/50 bg-cyan-50 px-4 py-3 flex items-start gap-3 shadow-sm"
      data-testid="gps-active-banner"
      role="status"
      aria-live="polite"
    >
      <div className="relative">
        <div className="absolute inset-0 rounded-full bg-cyan-400/30 animate-ping" />
        <div className="relative bg-cyan-500 text-white p-2 rounded-full">
          <Signal className="w-4 h-4" />
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-cyan-900 text-sm">
          GPS Tracking Active for Attendance Verification
        </div>
        <div className="text-xs text-cyan-800/80 mt-0.5 leading-relaxed">
          {error ? (
            <span className="text-red-700 flex items-center gap-1">
              <ShieldAlert className="w-3.5 h-3.5" /> {error}
            </span>
          ) : (
            <>
              We're recording your location every 90 seconds while this shift is active.
              Tracking stops automatically at clock-out.
            </>
          )}
        </div>
        <div className="text-[11px] text-cyan-700 mt-1 flex items-center gap-2 flex-wrap">
          <MapPin className="w-3 h-3" />
          {lastFix ? (
            <>
              Last fix: {lastFix.lat.toFixed(5)}, {lastFix.lng.toFixed(5)}
              <span className="text-cyan-600/60">· {pointCount} points</span>
            </>
          ) : (
            <span className="text-cyan-700/70">Acquiring first GPS fix…</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default GPSActiveBanner;
